import { createContext, useCallback, useContext, useRef } from 'react'

import { identity } from 'es-toolkit'
import { useStore } from 'zustand'

import {
  type DashboardStore,
  type DashboardStoreState,
  createDashboardStore,
} from '@/components/home/dashboard/store'
import { UploadFileDialog } from '@/components/library/dashboard/files/UploadFileDialog'
import { CreatePursuitDialog } from '@/components/pursuits/v3/dialogs/CreatePursuitDialog'

import { useSafePush } from '@/hooks/navigation/useSafePush'

import type { Pursuit } from '@/types/pursuit/Pursuit'

export const DashboardContext = createContext<DashboardStore | undefined>(
  undefined,
)

export const useHomeDashboardContext = () => {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error(
      'useHomeDashboardContext must be used within a DashboardContextProvider',
    )
  }
  return context
}

export const useHomeDashboardStore = <T,>(
  selector: (state: DashboardStoreState) => T = identity as (
    state: DashboardStoreState,
  ) => T,
) => {
  const context = useHomeDashboardContext()
  if (!context) {
    throw new Error(
      'useHomeDashboardStore must be used within a DashboardContextProvider',
    )
  }
  return useStore(context, selector)
}

export const useHomeDashboardCreatePursuitDialogIsOpen = () => {
  return useHomeDashboardStore((state) => state.createPursuitDialogIsOpen)
}

export const useHomeDashboardSetCreatePursuitDialogIsOpen = () => {
  return useHomeDashboardStore((state) => state.setCreatePursuitDialogIsOpen)
}

export const useHomeDashboardUploadLibraryFileDialogIsOpen = () => {
  return useHomeDashboardStore((state) => state.uploadLibraryFileDialogIsOpen)
}

export const useHomeDashboardSetUploadLibraryFileDialogIsOpen = () => {
  return useHomeDashboardStore(
    (state) => state.setUploadLibraryFileDialogIsOpen,
  )
}

export const useHomeDashboardOpenCreatePursuitDialog = () => {
  const setCreatePursuitDialogIsOpen =
    useHomeDashboardSetCreatePursuitDialogIsOpen()
  const openDialog = useCallback(
    () => setCreatePursuitDialogIsOpen(true),
    [setCreatePursuitDialogIsOpen],
  )
  return openDialog
}

export const useHomeDashboardOpenUploadLibraryFileDialog = () => {
  const setUploadLibraryFileDialogIsOpen =
    useHomeDashboardSetUploadLibraryFileDialogIsOpen()
  const openDialog = useCallback(
    () => setUploadLibraryFileDialogIsOpen(true),
    [setUploadLibraryFileDialogIsOpen],
  )
  return openDialog
}

const DashboardDialogs: React.FC = () => {
  const createPursuitDialogIsOpen = useHomeDashboardCreatePursuitDialogIsOpen()
  const setCreatePursuitDialogIsOpen =
    useHomeDashboardSetCreatePursuitDialogIsOpen()

  const uploadLibraryFileDialogIsOpen =
    useHomeDashboardUploadLibraryFileDialogIsOpen()
  const setUploadLibraryFileDialogIsOpen =
    useHomeDashboardSetUploadLibraryFileDialogIsOpen()

  const { safePush } = useSafePush()

  const handleNewPursuit = useCallback(
    (pursuit: Pursuit) => {
      safePush(`/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}`)
    },
    [safePush],
  )

  return (
    <>
      <CreatePursuitDialog
        open={createPursuitDialogIsOpen}
        onOpenChange={setCreatePursuitDialogIsOpen}
        onSuccess={handleNewPursuit}
      />
      <UploadFileDialog
        open={uploadLibraryFileDialogIsOpen}
        setOpen={setUploadLibraryFileDialogIsOpen}
      />
    </>
  )
}

export const DashboardContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const storeRef = useRef<DashboardStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createDashboardStore()
  }

  return (
    <DashboardContext.Provider value={storeRef.current}>
      {children}
      <DashboardDialogs />
    </DashboardContext.Provider>
  )
}
